import { useMutation } from 'react-query';
import usePlanStore, { CountryFormState } from 'store/planStore';
import moment from 'moment/moment';
import { updatePlan } from '../../../../../../api/services/mediaPlanning/updatePlan';
import { useError } from '../../../common/Errors/ErrorContext/ErrorContext';

const useUpdatePlan = (id?: string) => {
  const { generalInfoState } = usePlanStore();

  const { showError } = useError();

  const updatedStoredCountryForm = usePlanStore.getState().storedCountryForms;

  const convertDatesToISO = (
    form: Partial<CountryFormState>
  ): Partial<CountryFormState> => {
    const convert = (date?: string | null) =>
      date ? moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD') : date;

    return {
      ...form,
      startDate: convert(form.startDate) || '',
      youTubeConfig: form.youTubeConfig && {
        ...form.youTubeConfig,
        ...(process.env.REACT_APP_API_VERSION !== 'v3' && {
          skipShare: form.youTubeConfig?.skipValue,
          unskipShare: form.youTubeConfig?.unSkipValue,
        }),
      },
      metaConfig: form.metaConfig
        ? {
            ...form.metaConfig,
            // to omit migrations on back.
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            startDate: convert(form.metaConfig.startDate),
          }
        : undefined,
      offline: {
        ...form.offline,
        tv: form.offline?.tv
          ? {
              ...form.offline?.tv,
              startDate: moment(form.offline.tv.startDate, 'DD-MM-YYYY').format(
                'YYYY-MM-DD'
              ),
              endDate: moment(form.offline.tv.endDate, 'DD-MM-YYYY').format(
                'YYYY-MM-DD'
              ),
            }
          : undefined,
      },
    };
  };

  const convertCountryFormsToISO = (): Record<
    string,
    Partial<CountryFormState>
  > => {
    return Object.fromEntries(
      Object.entries(updatedStoredCountryForm).map(([country, form]) => [
        country,
        convertDatesToISO(form),
      ])
    );
  };

  const countrySetupWithISO = convertCountryFormsToISO();

  const {
    mutate: updateMediaPlan,
    mutateAsync: asyncUpdateMediaPlan,
    data: response,
    isLoading,
    isError,
  } = useMutation(
    async (passedId: string | null) => {
      return updatePlan({
        body: {
          campaignName: generalInfoState.campaignName,
          division: generalInfoState.division,
          brand: generalInfoState.brand,
          category: generalInfoState.category,
          countrySetup: countrySetupWithISO,
        },
        id: id ?? passedId ?? '',
      });
    },
    {
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message || error.message || 'Unknown error';
        showError(errorMessage);
      },
    }
  );

  return {
    updateMediaPlan,
    asyncUpdateMediaPlan,
    data: response?.data,
    isLoading,
    isError,
  };
};

export default useUpdatePlan;
